import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Mail, CreditCard, FileQuestion, Code2, Fingerprint, Settings2 } from 'lucide-react';

const HelpAndSupport = () => {
    return (
        <div className="p-6 max-w-4xl mx-auto space-y-6">
            {/* Contact Alert */}
            <Alert className="bg-blue-50 border-blue-200">
                <Mail className="h-4 w-4" />
                <AlertTitle>Need direct assistance?</AlertTitle>
                <AlertDescription>
                    Contact our support team at <span className="font-semibold">support@klasite.com</span> - we respond in less than 24 hours.
                </AlertDescription>
            </Alert>

            {/* Main Help Sections */}
            <div className="grid gap-6">
                {/* Getting Started */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Settings2 className="h-5 w-5" />
                            Getting Started
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="item-1">
                                <AccordionTrigger>How do I start analyzing websites?</AccordionTrigger>
                                <AccordionContent>
                                    <ol className="list-decimal pl-5 space-y-2">
                                        <li>Upload a CSV file containing website URLs</li>
                                        <li>Select an analysis type (standard or custom)</li>
                                        <li>Click "Analyze" to begin the process</li>
                                        <li>Download your results when the analysis is complete</li>
                                    </ol>
                                </AccordionContent>
                            </AccordionItem>

                            <AccordionItem value="item-2">
                                <AccordionTrigger>What should my CSV file look like?</AccordionTrigger>
                                <AccordionContent>
                                    <div className="space-y-4">
                                        <p>Your CSV file should have a column named "url" containing the website URLs to analyze. Make sure URLs include "http://" or "https://".</p>

                                        <div className="bg-slate-50 p-4 rounded-md">
                                            <p className="font-medium mb-2">Example CSV format:</p>
                                            <div className="bg-white p-3 rounded border text-sm font-mono">
                                                <table className="w-full">
                                                    <thead>
                                                        <tr>
                                                            <th className="text-left border-b-2 border-gray-200 pb-2">url</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td className="pt-2">https://example.com</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="pt-2">https://example.org</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="pt-2">https://example.net</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <p className="text-sm text-slate-600">Note: You can include additional columns in your CSV, but the 'url' column is required for analysis.</p>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </CardContent>
                </Card>

                {/* Credits & Billing */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <CreditCard className="h-5 w-5" />
                            Credits & Billing
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="item-1">
                                <AccordionTrigger>How do credits work?</AccordionTrigger>
                                <AccordionContent>
                                    <ul className="list-disc pl-5 space-y-2">
                                        <li>Each website analysis costs 1 credit</li>
                                        <li>Creating custom analysis types costs {10} credits</li>
                                        <li>New users start with 100 free credits</li>
                                        <li>Purchase additional credits from the "Buy Credits" page</li>
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </CardContent>
                </Card>

                {/* Custom Analysis Types */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Code2 className="h-5 w-5" />
                            Custom Analysis Types
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="item-1">
                                <AccordionTrigger>How do I create a custom analysis type?</AccordionTrigger>
                                <AccordionContent>
                                    <ol className="list-decimal pl-5 space-y-2">
                                        <li>Navigate to the Custom Analysis page</li>
                                        <li>Click "Create New Analysis Type"</li>
                                        <li>Define your analysis criteria and questions</li>
                                        <li>Save your custom type</li>
                                    </ol>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </CardContent>
                </Card>

                {/* Security & Privacy */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Fingerprint className="h-5 w-5" />
                            Security & Privacy
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="item-1">
                                <AccordionTrigger>How is my data protected?</AccordionTrigger>
                                <AccordionContent>
                                    <ul className="list-disc pl-5 space-y-2">
                                        <li>All data is encrypted in transit and at rest</li>
                                        <li>We use Supabase for secure data storage</li>
                                        <li>Analysis results are only accessible to your account</li>
                                        <li>We never share your data with third parties</li>
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </CardContent>
                </Card>

                {/* Troubleshooting */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileQuestion className="h-5 w-5" />
                            Troubleshooting
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="item-1">
                                <AccordionTrigger>Common Issues</AccordionTrigger>
                                <AccordionContent>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold">Analysis fails to start</h4>
                                            <p>Check that:</p>
                                            <ul className="list-disc pl-5">
                                                <li>You have sufficient credits</li>
                                                <li>Your CSV file is properly formatted</li>
                                                <li>All URLs are valid and accessible</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">Results not downloading</h4>
                                            <p>Try:</p>
                                            <ul className="list-disc pl-5">
                                                <li>Refreshing the page</li>
                                                <li>Checking your browser's download settings</li>
                                                <li>Using a different browser</li>
                                            </ul>
                                        </div>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default HelpAndSupport;